import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Lock, Ticket, Mail } from "lucide-react";
import "@/Gate.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccessGate = ({ children }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestData, setRequestData] = useState({ nom: "", email: "", message: "" });

  useEffect(() => {
    setUnlocked(localStorage.getItem("app_access_granted") === "true");
    setChecking(false);
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/access/verify`, { code });
      if (response.data.valid) {
        localStorage.setItem("app_access_granted", "true");
        setUnlocked(true);
        toast.success("Accès débloqué");
      } else {
        toast.error("Code d'accès incorrect");
      }
    } catch (error) {
      toast.error("Erreur lors de la vérification du code");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/access/requests`, requestData);
      toast.success("Votre demande a été envoyée");
      setRequestData({ nom: "", email: "", message: "" });
      setShowRequestForm(false);
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la demande");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) return null;
  if (unlocked) return children;

  return (
    <div className="gate-container">
      <Toaster position="top-right" />
      <div className="gate-card" data-testid="access-gate-card">
        <div className="gate-icon-wrapper">
          {showRequestForm ? <Mail className="gate-icon" /> : <Lock className="gate-icon" />}
        </div>
        <div className="gate-logo">
          <Ticket className="gate-logo-icon" />
          <h1>Gestion des Réservations</h1>
        </div>

        {!showRequestForm ? (
          <>
            <p className="gate-subtitle">Entrez votre code d'accès pour continuer</p>
            <form onSubmit={handleVerify} className="gate-form">
              <Input
                data-testid="access-code-input"
                type="password"
                placeholder="Code d'accès"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
              />
              <Button
                data-testid="access-code-submit-button"
                type="submit"
                disabled={submitting}
                className="btn-primary gate-submit-btn"
              >
                {submitting ? "Vérification..." : "Déverrouiller"}
              </Button>
            </form>
            <button
              data-testid="request-access-link"
              className="gate-link"
              onClick={() => setShowRequestForm(true)}
            >
              Vous n'avez pas de code ? Demander l'accès
            </button>
          </>
        ) : (
          <>
            <p className="gate-subtitle">Envoyez-nous votre demande d'accès</p>
            <form onSubmit={handleRequestSubmit} className="gate-form">
              <div className="form-field">
                <Label htmlFor="req-nom">Nom *</Label>
                <Input
                  data-testid="request-name-input"
                  id="req-nom"
                  value={requestData.nom}
                  onChange={(e) => setRequestData({ ...requestData, nom: e.target.value })}
                  required
                />
              </div>
              <div className="form-field">
                <Label htmlFor="req-email">Email *</Label>
                <Input
                  data-testid="request-email-input"
                  id="req-email"
                  type="email"
                  value={requestData.email}
                  onChange={(e) => setRequestData({ ...requestData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-field">
                <Label htmlFor="req-message">Message</Label>
                <Textarea
                  data-testid="request-message-input"
                  id="req-message"
                  rows={3}
                  value={requestData.message}
                  onChange={(e) => setRequestData({ ...requestData, message: e.target.value })}
                  placeholder="Expliquez pourquoi vous souhaitez accéder à l'application"
                />
              </div>
              <Button
                data-testid="request-access-submit-button"
                type="submit"
                disabled={submitting}
                className="btn-primary gate-submit-btn"
              >
                {submitting ? "Envoi..." : "Envoyer la demande"}
              </Button>
            </form>
            <button
              data-testid="back-to-code-link"
              className="gate-link"
              onClick={() => setShowRequestForm(false)}
            >
              Retour au code d'accès
            </button>
          </>
        )}
      </div>
    </div>
  );
};
